/**
 * Hero Organism Component
 * Hero section with heading, description, CTA, and hero image
 * Supports separate dark/light background images that swap with theme
 */

import React, { useState, useEffect } from "react";
import { storyblokEditable } from "@/lib/blocks";
import { Container } from "@/components/atoms/Container";
import { Heading } from "@/components/atoms/Heading";
import { Text } from "@/components/atoms/Text";
import { Button } from "@/components/atoms/Button";
import { Image } from "@/components/atoms/Image";
import type { HeroBlok } from "@/types";

export interface HeroProps {
  blok: HeroBlok;
}

export const Hero: React.FC<HeroProps> = ({ blok }) => {
  const {
    eyebrow,
    heading,
    highlighted_words,
    description,
    cta_button,
    hero_image,
    hero_image_light,
    hero_illustration,
  } = blok;

  const hasIllustration = hero_illustration?.filename;

  // Track current theme to swap background images
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Pick the right background image for the current theme
  const bgImage = isDark
    ? hero_image?.filename
    : (hero_image_light?.filename || hero_image?.filename);

  // Replace highlighted words in heading with styled version
  const renderHeading = () => {
    if (!highlighted_words) return heading;

    const wordsToHighlight = highlighted_words.split(",").map((w) => w.trim());
    const pattern = new RegExp(`(${wordsToHighlight.join("|")})`, "gi");
    const parts = heading.split(pattern);

    return parts.map((part, index) => {
      const isHighlighted = wordsToHighlight.some(
        (word) => part.toLowerCase() === word.toLowerCase()
      );
      return isHighlighted ? (
        <span key={index} className="text-primary dark:text-white">
          {part}
        </span>
      ) : (
        <span key={index}>{part}</span>
      );
    });
  };

  return (
    <section
      {...storyblokEditable(blok)}
      className="bg-theme-surface text-theme-text py-8 sm:py-10 md:py-14 lg:py-20 xl:py-24 pb-64 sm:pb-72 md:pb-14 lg:pb-20 xl:pb-24 relative overflow-hidden"
      style={{
        backgroundImage: bgImage ? `url(${bgImage})` : undefined,
        backgroundSize: "contain",
        backgroundPosition: "bottom center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Container className="relative">
        <div className={`flex flex-col ${hasIllustration ? 'lg:flex-row lg:items-center' : ''} gap-8 md:gap-12 lg:gap-16`}>
          {/* Content */}
          <div className={`flex flex-col gap-4 md:gap-6 lg:gap-8 ${hasIllustration ? 'lg:flex-1' : 'md:max-w-lg lg:max-w-2xl'}`}>
            {eyebrow && (
              <Text className="text-primary font-bold uppercase tracking-wide text-xs md:text-sm">
                {eyebrow}
              </Text>
            )}

            <Heading
              level="h1"
              color="black"
              className="leading-none tracking-tight uppercase text-black dark:text-primary"
            >
              {renderHeading()}
            </Heading>

            <Text size="base" color="text-black dark:text-primary" className="max-w-xl md:max-w-2xl font-space-mono text-sm md:text-xl leading-tight">
              {description}
            </Text>

            {/* CTA Buttons */}
            {cta_button && cta_button.length > 0 && (
              <div className="flex gap-3 md:gap-4 flex-wrap mt-2 md:mt-4">
                {cta_button.map((cta, index) => (
                  <Button
                    key={index}
                    href={cta.button_link?.cached_url || cta.button_link?.url}
                    variant={cta.button_variant || "primary"}
                    size="md"
                  >
                    {cta.button_text}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Hero Illustration (right column) */}
          {hasIllustration && (
            <div className="lg:flex-1 w-full max-w-lg lg:max-w-none mx-auto">
              <Image
                src={hero_illustration.filename}
                alt={hero_illustration.alt || heading}
                className="w-full h-auto"
              />
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export default Hero;
